import { useEffect, useLayoutEffect, useRef } from 'react';

const scrollPositions = new Map();

/**
 * Custom hook to manage the scroll position of a sidebar navigation element.
 */
export default function useSidebarNavScroll(storageKey, activeItemId, layoutKey) {
  const navRef = useRef(null);

  useEffect(() => {
    const nav = navRef.current;
    if (!nav) {
      return undefined;
    }

    const handleScroll = () => {
      scrollPositions.set(storageKey, nav.scrollTop);
    };

    nav.addEventListener('scroll', handleScroll, { passive: true });
    return () => nav.removeEventListener('scroll', handleScroll);
  }, [storageKey]);

  // Restore the last known scroll position on mount, and whenever the layout or active item changes.
  useLayoutEffect(() => {
    const nav = navRef.current;
    if (!nav) {
      return;
    }

    const saved = scrollPositions.get(storageKey);
    if (typeof saved === 'number') {
      nav.scrollTop = saved;
    }
  }, [storageKey, activeItemId, layoutKey]);

  useEffect(() => {
    const nav = navRef.current;
    if (!nav) {
      return undefined;
    }

    const ensureActiveVisible = () => {
      const activeEl = nav.querySelector('[data-sidebar-active="true"]');
      if (activeEl) {
        activeEl.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }
      scrollPositions.set(storageKey, nav.scrollTop);
    };

    const frameId = window.requestAnimationFrame(ensureActiveVisible);
    // Group expand uses a ~300ms grid transition; re-check after it settles.
    const timeoutId = window.setTimeout(ensureActiveVisible, 320);

    return () => {
      window.cancelAnimationFrame(frameId);
      window.clearTimeout(timeoutId);
    };
  }, [storageKey, activeItemId, layoutKey]);

  return navRef;
}
